import { InsightContentSettingType } from 'components/insightContent/types'
import { PivotWithCellMarker } from 'components/widget/CrosstabWidgets/types'
import { Pivot } from 'schemas/PivotSchema'
import { Metric } from 'schemas/MetricSchema'
import ReturnStatus from 'enums/ReturnStatus'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { isVoc } from './viewOptionUtils'

const resolveWeightFromPivotThemeInfos = (
  detailSetting: InsightContentSettingType,
  crossTab: Pivot
) => {
  const { pivotThemeInfos, viewOption } = detailSetting
  if (viewOption === InsightViewOption.ENGAGEMENT_RATIO_ONLY) {
    return 1
  }

  const { themeId } = crossTab
  if (!themeId) {
    return 1
  }

  if (!pivotThemeInfos) {
    return 1
  }

  const theme = pivotThemeInfos.find((pivotTheme) => pivotTheme.themeId === themeId)
  if (!theme || !theme.weighting) {
    return 1
  }

  return theme.weighting / 100
}

const resolveWeightFromPivotRowThemeInfos = (
  detailSetting: InsightContentSettingType,
  crossTab: Pivot
) => {
  const { pivotRowThemeInfos, viewOption } = detailSetting
  if (viewOption === InsightViewOption.ENGAGEMENT_RATIO_ONLY) {
    return 1
  }

  const { rowThemeId } = crossTab
  if (!rowThemeId) {
    return 1
  }

  if (!pivotRowThemeInfos) {
    return 1
  }

  const theme = pivotRowThemeInfos.find((pivotTheme) => pivotTheme.themeId === rowThemeId)
  if (!theme || !theme.weighting) {
    return 1
  }

  return theme.weighting / 100
}

export const resolveAverage = (
  detailSetting: InsightContentSettingType,
  crossTabs: PivotWithCellMarker[]
) => {
  const resolveWeight = (crossTab: Pivot) =>
    resolveWeightFromPivotThemeInfos(detailSetting, crossTab) *
    resolveWeightFromPivotRowThemeInfos(detailSetting, crossTab)

  const validCrossTabs = crossTabs?.filter((crossTab: Pivot) => {
    const metrics: Metric[] = crossTab.metrics as Metric[]
    return metrics?.[0]?.dataset?.[0]?.status === ReturnStatus.NORMAL
  })

  const totalSum = validCrossTabs?.reduce((accumulator: number, crossTab: Pivot) => {
    const metrics: Metric[] = crossTab.metrics as Metric[]
    const value = metrics?.[0]?.dataset?.[0]?.set?.[0] || 0

    return accumulator + value * resolveWeight(crossTab)
  }, 0)

  const totalWeight = validCrossTabs?.reduce(
    (accumulator: number, crossTab: Pivot) => accumulator + resolveWeight(crossTab),
    0
  )

  return totalWeight === 0 ? 0 : totalSum / totalWeight
}

export const formatDifferenceOfAverage = (
  averageOfAllItems: number,
  selectedItemValue: number,
  viewOption: InsightViewOptionStrings
) => {
  const isVocViewOption = isVoc(viewOption)

  const diffValue = isVocViewOption
    ? Math.round(selectedItemValue * 10) / 10 - Math.round(averageOfAllItems * 10) / 10
    : Math.round(selectedItemValue) - Math.round(averageOfAllItems)

  const formatter =
    isVocViewOption && !Number.isInteger(diffValue)
      ? new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1
        })
      : new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })

  if (diffValue === 0) {
    return '+0'
  }

  if (diffValue > 0) {
    return `+${formatter.format(diffValue)}`
  }

  return formatter.format(diffValue)
}

export const resolveDifferenceOfAverage = (
  detailSetting: InsightContentSettingType,
  allItems: PivotWithCellMarker[],
  selectedItem: PivotWithCellMarker[]
) => {
  const { viewOption } = detailSetting

  const averageOfAllItems = resolveAverage(detailSetting, allItems)
  const selectedItemValue = resolveAverage(detailSetting, selectedItem)

  return formatDifferenceOfAverage(averageOfAllItems, selectedItemValue, viewOption)
}
